import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const TokenPurchasePage = () => {
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const tokenAmount = Math.floor(parseInt(amount || 0) / 1000);

  const handlePurchase = async (e) => {
    e.preventDefault();
    
    const amountNum = parseInt(amount);
    if (amountNum < 1000) {
      toast.error("Jumlah minimal 1000 IDR");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/tokens/purchase`,
        { amount: amountNum },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      await refreshUser();
      setAmount("");
      navigate("/home");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Pembelian gagal");
    } finally {
      setLoading(false);
    }
  };

  const quickAmounts = [10000, 25000, 50000, 100000, 250000, 500000];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-gradient-ltb-r text-white p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center">
          <button onClick={() => navigate("/home")} className="mr-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold">Beli Token</h1>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="text-center mb-6">
            <div className="bg-yellow-400 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-yellow-800" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Beli Token</h2>
            <p className="text-gray-600">1 Token = Rp 1.000</p>
          </div>

          <form onSubmit={handlePurchase} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jumlah (IDR)
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                min="1000"
                step="1000"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-semibold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="10000"
              />
              {tokenAmount > 0 && (
                <p className="mt-2 text-center text-lg font-semibold text-blue-600">
                  = {tokenAmount} Token
                </p>
              )}
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Pilih Cepat:</p>
              <div className="grid grid-cols-3 gap-3">
                {quickAmounts.map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setAmount(amt.toString())}
                    className="bg-gray-100 hover:bg-blue-50 border-2 border-gray-300 hover:border-blue-500 p-3 rounded-lg transition-colors"
                  >
                    <div className="text-sm font-semibold text-gray-800">
                      Rp {amt.toLocaleString('id-ID')}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {amt / 1000} Token
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>Info:</strong> Ini adalah simulasi pembelian. Token akan langsung ditambahkan ke akun Anda.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !amount}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Memproses..." : "Beli Sekarang"}
            </button>
          </form>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-bold text-gray-800 mb-3">Kegunaan Token:</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span><strong>Voting:</strong> 2 Token per vote</span>
            </li>
            <li className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span><strong>Foto:</strong> 5 Token per foto</span>
            </li>
            <li className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span><strong>Tiket:</strong> Harga sesuai event</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TokenPurchasePage;
